import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import { maps } from "../data/maps";
import { useLang } from "../i18n";

const chapterMeta = [
  { org: "Brainchimps", map: "germany", coords: "51.16° N / 10.45° E", credit: true },
  { org: "CodingCops", map: "usa", coords: "39.83° N / 98.58° W", credit: true },
  { org: "Kiwi Creations", map: "lahore", coords: "31.52° N / 74.35° E", credit: true },
  { org: "FAST-NUCES", map: "fast", coords: "31.48° N / 74.30° E", credit: true },
];

function ChapterMap({ id }) {
  const map = maps[id];
  return (
    <div className="chapter-map" aria-hidden="true">
      <svg viewBox="0 0 1000 1000">
        {map.paths.map((path, index) => (
          <path key={index} className={`map-line map-${path.k}`} d={path.d} />
        ))}
        {map.marker ? (
          <g className="map-mark" transform={`translate(${map.marker.x} ${map.marker.y})`}>
            <path d="M-22 -22H22V22H-22Z" />
            <path d="M-40 0H-26M26 0H40M0-40V-26M0 26V40" />
          </g>
        ) : null}
      </svg>
    </div>
  );
}

function Experience() {
  const { t } = useLang();
  const chapters = chapterMeta.map((meta, index) => ({ ...meta, ...t.experience.chapters[index] }));
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const [seen, setSeen] = useState(() => new Set());

  useEffect(() => {
    const frame = requestAnimationFrame(() => setSeen(new Set([0])));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const root = trackRef.current;
    if (!root) return undefined;
    const nodes = root.querySelectorAll("[data-chapter]");
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const index = Number(entry.target.getAttribute("data-chapter"));
        setActive(index);
        setSeen((current) => {
          if (current.has(index)) return current;
          const next = new Set(current);
          next.add(index);
          return next;
        });
      });
    }, { root, threshold: 0.6 });
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  const scrollToChapter = (index) => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const node = trackRef.current?.querySelector(`[data-chapter="${index}"]`);
    node?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  };

  return (
    <div className="experience-shell">
      <div className="experience-progress" aria-hidden="true">
        <span style={{ transform: `scaleX(${(active + 1) / chapters.length})` }} />
      </div>
      <Header />
      <div className="experience-track" ref={trackRef}>
        {chapters.map((chapter, index) => (
          <section
            key={chapter.org}
            className={`chapter${seen.has(index) ? " is-seen" : ""}${index === active ? " is-active" : ""}`}
            data-chapter={index}
            aria-labelledby={`chapter-${index}`}
          >
            <ChapterMap id={chapter.map} />
            <div className="chapter-frame" aria-hidden="true" />
            <div className="chapter-sweep" aria-hidden="true" />
            <div className="chapter-inner">
              <p className="chapter-index text-faint font-mono text-[12px]">
                <span className="text-accent">{chapter.when}</span>
              </p>
              <h1 id={`chapter-${index}`} className="chapter-title">{chapter.title}</h1>
              <p className="chapter-org">{chapter.org}</p>
              <p className="text-muted chapter-body">{chapter.body}</p>
              {chapter.tags.length > 0 ? (
                <ul className="chapter-tags">
                  {chapter.tags.map((tag) => (
                    <li key={tag} className="skill-chip">{tag}</li>
                  ))}
                </ul>
              ) : null}
              {index < chapters.length - 1 ? (
                <button type="button" className="chapter-next" onClick={() => scrollToChapter(index + 1)}>
                  {t.experience.next}
                </button>
              ) : (
                <Link to="/" className="chapter-next">{t.experience.home}</Link>
              )}
            </div>
            <p className="chapter-place">
              <strong>{chapter.place}</strong>
              <span>{chapter.coords}</span>
              {chapter.credit ? <span className="chapter-credit">© OpenStreetMap</span> : null}
            </p>
          </section>
        ))}
      </div>
      <nav className="chapter-rail" aria-label={t.experience.label}>
        {chapters.map((chapter, index) => (
          <button
            key={chapter.org}
            type="button"
            className="rail-btn"
            aria-label={`${chapter.title} ${t.experience.at} ${chapter.org}`}
            aria-current={index === active ? "true" : undefined}
            onClick={() => scrollToChapter(index)}
          >
            <span />
          </button>
        ))}
      </nav>
    </div>
  );
}

export default Experience;
